/* eslint-disable max-len */
import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  ACTIVE,
  COMPLETED,
  CURRENT_STEP,
  INACTIVE,
  ProgressBarContext,
  SHOW_PROGRESS_BAR,
  UPDATE_STEP,
  SET_STEP
} from "../ProgressBarProvider";
import {
  getAccordionStatuses,
  getIsMultiPartnerJourney,
  getPersona,
  hasPartnersDetailInfo
} from "../../../store/selectors/appConfig";
import routes from "../../../routes";
import {
  getIsMultipleSignatoriesJourney,
  getSelectedStakeholder
} from "../../../store/selectors/stakeholders";
import { checkIfEfrNotRequiredForNonSignatory } from "../../../utils/checkIfEfrNotRequiredForNonSignatory";

const matchPathWithParams = (pattern, path) => {
  const patternParts = pattern.split("/");
  const pathParts = path.split("/");

  if (patternParts.length !== pathParts.length) {
    return false;
  }

  return patternParts.every((part, index) => {
    return part.startsWith(":") || part === pathParts[index];
  });
};

// Helper function to get the current step index
const getCurrentStepIndex = (pathname, routes, context, isNonSignatoryJourney) => {
  switch (pathname) {
    case routes.additionalInfoComponent:
      return getAdditionalInfoComponentStep(context);
    case routes.additionalCompanyInformation:
      return 2;
    case routes.additionalStakeholderInformation:
      return isNonSignatoryJourney ? 5 : 4;
    default:
      return context.currentStepIndex + 1;
  }
};

// Helper function to get the step index for additional info component
const getAdditionalInfoComponentStep = context => {
  const {
    isStakeholderInfoCompleted,
    isCompanyAdditionalInfoCompleted,
    steps,
    isCurrentPathIncluded
  } = context;
  if (isStakeholderInfoCompleted) {
    return steps[isCurrentPathIncluded].totalSteps;
  } else if (isCompanyAdditionalInfoCompleted) {
    return 3;
  } else {
    return 1;
  }
};

export const useProgressBar = isMobile => {
  const { state, dispatch } = useContext(ProgressBarContext);
  const { steps, showProgressBar, currentStepName } = state;
  const { pathname } = useLocation();
  const accordionStatus = useSelector(getAccordionStatuses);
  const persona = useSelector(getPersona);
  const selectedStakeholder = useSelector(getSelectedStakeholder);
  const partnersDetailInfoAvailable = useSelector(hasPartnersDetailInfo);
  const isMultipleSignatoriesJourney = useSelector(getIsMultipleSignatoriesJourney);

  const isMultiPartnerJourney = useSelector(getIsMultiPartnerJourney);

  const isNonSignatoryWithoutEFRJourney =
    isMultiPartnerJourney &&
    selectedStakeholder &&
    Object.keys(selectedStakeholder).length &&
    checkIfEfrNotRequiredForNonSignatory(selectedStakeholder);

  const isNonSignatoryJourney =
    isMultiPartnerJourney && selectedStakeholder?.kycDetails?.isSignatory === false;
  const isPrimaryContact =
    isMultiPartnerJourney && selectedStakeholder?.kycDetails?.isPrimaryContact;

  useEffect(() => {
    if (!persona) {
      return;
    }
    dispatch({
      type: SET_STEP,
      payload: {
        persona,
        isNonSignatoryWithoutEFRJourney,
        isNonSignatoryJourney,
        hasPartnersDetailInfo: partnersDetailInfoAvailable,
        isMultipleSignatoriesJourney,
        isPrimaryContact
      }
    });
  }, [
    persona,
    isNonSignatoryJourney,
    isNonSignatoryWithoutEFRJourney,
    partnersDetailInfoAvailable,
    pathname,
    isMultipleSignatoriesJourney
  ]);

  useEffect(() => {
    const isCurrentPathIncluded = steps.findIndex(step =>
      step.substeps.some(substep => matchPathWithParams(substep, pathname))
    );

    dispatch({
      type: SHOW_PROGRESS_BAR,
      payload: isCurrentPathIncluded
    });

    if (isCurrentPathIncluded === -1) {
      return;
    }

    const currentPathName = steps[isCurrentPathIncluded].name;
    dispatch({
      type: CURRENT_STEP,
      payload: currentPathName
    });

    const statuses = JSON.parse(accordionStatus);
    const { companyAdditionalInfoStatus, addionalStakeholderInfoStatus } = statuses;
    const isCompanyAdditionalInfoCompleted = isNonSignatoryJourney
      ? true
      : companyAdditionalInfoStatus === "completed";
    const isStakeholderInfoCompleted = addionalStakeholderInfoStatus === "completed";

    let currentStepIndex = steps[isCurrentPathIncluded].substeps.findIndex(substep =>
      matchPathWithParams(substep, pathname)
    );

    let value = 0;
    let status = INACTIVE;

    for (let i = 0; i < steps.length; i++) {
      if (i < isCurrentPathIncluded) {
        status = COMPLETED;
        dispatch({
          type: UPDATE_STEP,
          payload: {
            index: i,
            value: 100,
            status,
            subStepIndex: steps[i].totalSteps - 1
          }
        });
      } else {
        status = INACTIVE;
        dispatch({
          type: UPDATE_STEP,
          payload: {
            index: i,
            value: 0,
            status,
            subStepIndex: 0
          }
        });
      }
    }

    // Context object to pass to helper functions
    const context = {
      isStakeholderInfoCompleted,
      isCompanyAdditionalInfoCompleted,
      steps,
      isCurrentPathIncluded,
      currentStepIndex
    };

    currentStepIndex = getCurrentStepIndex(pathname, routes, context, isNonSignatoryJourney);

    value = Math.floor((currentStepIndex / steps[isCurrentPathIncluded].totalSteps) * 100);

    dispatch({
      type: UPDATE_STEP,
      payload: {
        index: isCurrentPathIncluded,
        value,
        status: ACTIVE,
        subStepIndex: currentStepIndex - 1
      }
    });
  }, [pathname, isMobile, persona, isNonSignatoryJourney, accordionStatus]);

  return { showProgressBar, steps, currentStepName };
};
